import React, { SyntheticEvent, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  CaseStatus,
  ExternalUser,
  ExternalUserRoleRole,
  useFetchAllCasesLazyQuery,
  useFetchExternalUserQuery
} from '../../generated'
import { useDebounce } from 'use-debounce'
import { AppContext, CaseContext } from '../context'
import { CaseActionType } from '../components/cases/caseReducer'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { ActionType } from '../context/AppContextReducer'
import { Box, Button, Typography } from '@mui/material'
import { ArrowBackIos, Edit } from '@mui/icons-material'
import { TabItem, TabsContainer } from '../theme/styleComponents'
import palette from '../theme/palette'
import { a11yProps, TabPanel } from '../components/common/TabPanel'
import { CommonLoader } from '../components/common/CommonLoader'
import ClientGeneralCard from '../components/externalUsers/client/ClientGeneralCard'
import { AllCases } from '../components/cases/allCases'
import { EditAdversary } from '../components/externalUsers/EditAdversary'
import { EditJudge } from '../components/externalUsers/EditJudge'

const ContactsDetailContainer = () => {
  const navigate = useNavigate()
  const { type, id } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const [contact, setContact] = useState<ExternalUser | null>(null)

  const searchValue = searchParams.get('searchValue')
  const activeTab = parseInt(searchParams.get('activeTab') || '0')
  const [debouncedSearch] = useDebounce(searchValue, 500)

  const { dispatch: reducerDispatch, currentPage } = useContext(AppContext)
  const { pageValue, lawTypeId, dispatch: caseDispatch, caseFilter, sortCasesByUser, orderBy } = useContext(CaseContext)

  const handleBack = () => navigate(-1)
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    caseDispatch({ type: CaseActionType.SET_PAGE_VALUE, pageValue: newValue })
  }

  const handleOpenEditModal = () => reducerDispatch({ type: ActionType.SET_IS_EDIT, isEdit: true })

  const [fetchAllCases, { loading }] = useFetchAllCasesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllCases } = data

        if (fetchAllCases) {
          const { cases, pagination } = fetchAllCases

          if (cases) {
            caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: cases || [] })
            if (!contact) {
              const clientInfo = cases
                .flatMap(matter => matter?.caseToExternalUserToExternalUserRole || [])
                .find(
                  item =>
                    item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles &&
                    item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles[0]?.externalUserRole?.role ===
                      ExternalUserRoleRole.Client
                )?.externalUserToExternalUserRole.externalUser

              if (clientInfo) setContact(clientInfo as ExternalUser)
            }
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  })

  const { refetch } = useFetchExternalUserQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      externalUserInput: {
        id: id || ''
      }
    },
    onCompleted: data => {
      const { fetchExternalUser } = data || {}
      const { externalUser } = fetchExternalUser || {}
      if (externalUser) setContact(externalUser)
    }
  })

  const renderEditModal = () => {
    switch (type) {
      case 'adversary':
        return (
          <EditAdversary
            userDetails={contact}
            contactRefetch={refetch}
            editClientNumberOnly={false}
            modalTitle="Adversary Details"
            successMessage="Adversary updated!"
            isEdit
          />
        )
      case 'contact':
        return (
          <EditAdversary
            userDetails={contact}
            contactRefetch={refetch}
            editClientNumberOnly={false}
            modalTitle="Contact Details"
            successMessage="Contact updated!"
            isEdit
          />
        )

      case 'judge':
        return <EditJudge userDetails={contact} refetch={refetch} isEdit />
    }
  }

  useEffect(() => {
    if (pageValue === 1 || activeTab === 1) {
      fetchAllCases({
        variables: {
          casesInput: {
            paginationOptions: {
              page: currentPage,
              limit: 50
            },
            orderBy,
            status: caseFilter === 'close' ? CaseStatus.Completed : CaseStatus.Active,
            searchValue: debouncedSearch,
            lawTypeId: lawTypeId,
            clientId: id
          }
        }
      })
    }
    return () => {}
    // eslint-disable-next-line
  }, [lawTypeId, caseFilter, debouncedSearch, currentPage, sortCasesByUser?.id, orderBy, type, activeTab, pageValue])

  useEffect(() => {
    return () => {
      caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
      return reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIos sx={{ width: '13px' }} />}
          sx={{
            color: '#5B5675',
            fontWeight: 600,
            fontSize: '14px',
            ml: -1
          }}
          onClick={handleBack}
        >
          BACK
        </Button>
      </Box>

      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant="h6">
          {!contact
            ? 'Loading...'
            : `${contact?.firstName} ${contact?.middleName || ''}  ${contact?.lastName} ${contact?.suffix || ''} `}
        </Typography>

        <Button variant="outlined" color="primary" onClick={handleOpenEditModal}>
          <Edit color="primary" fontSize="small" />
          <Typography color={'primary'} pl={0.5} fontWeight={'medium'}>
            Edit
          </Typography>
        </Button>
      </Box>

      <TabsContainer
        value={activeTab ?? pageValue}
        onChange={handleChange}
        aria-label="user tab"
        TabIndicatorProps={{ sx: { background: `${palette.darkRed}` } }}
        sx={{ paddingTop: '20px' }}
      >
        <TabItem
          label="General"
          {...a11yProps(0, 'contact')}
          iconPosition="start"
          onClick={() => {
            caseDispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            const zero = 0
            searchParams.set('activeTab', zero.toString())
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', currentPage.toString())
            setSearchParams(searchParams)
          }}
        />

        <TabItem
          label="Matters"
          {...a11yProps(1, 'contact')}
          iconPosition="start"
          onClick={() => {
            caseDispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            searchParams.set('activeTab', '1')
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', currentPage.toString())
            setSearchParams(searchParams)
          }}
        />
      </TabsContainer>

      <TabPanel value={activeTab ?? pageValue} index={0}>
        {loading ? <CommonLoader /> : <ClientGeneralCard client={contact} />}
      </TabPanel>

      <TabPanel value={activeTab ?? pageValue} index={1}>
        <AllCases loading={loading} buttonLink={`/matters/create`} />
      </TabPanel>

      {renderEditModal()}
    </Box>
  )
}

export default ContactsDetailContainer
